import React, { useState, useEffect } from 'react';
import {
  Activity,
  Send,
  Timer,
  CheckCircle2,
  XCircle,
  Sparkles,
  Flame
} from 'lucide-react';
import { SessionGameRound, SessionPlayerState } from '../../../types/session-games';
import { playCorrectSound, playWrongSound } from '../../../utils/soundEffects';

interface WordTennisViewProps {
  round: SessionGameRound;
  myPlayer: SessionPlayerState;
  partnerPlayer?: SessionPlayerState;
  onSubmit: (result: {
    answer: string;
    isCorrect: boolean;
    points: number;
  }) => void;
  isRoundResult: boolean;
}

export const WordTennisView: React.FC<WordTennisViewProps> = ({
  round,
  myPlayer,
  partnerPlayer,
  onSubmit,
  isRoundResult
}) => {
  const [wordInput, setWordInput] = useState('');
  const [rallyWords, setRallyWords] = useState<string[]>([]);
  const [secondsLeft, setSecondsLeft] = useState(15);
  const [submitted, setSubmitted] = useState(false);

  useEffect(() => {
    setWordInput('');
    setSubmitted(false);
    setSecondsLeft(15);
  }, [round.id]);

  // Turn shot clock
  useEffect(() => {
    if (submitted || isRoundResult) return;
    const interval = setInterval(() => {
      setSecondsLeft((prev) => {
        if (prev <= 1) {
          clearInterval(interval);
          handleTimeOut();
          return 0;
        }
        return prev - 1;
      });
    }, 1000);

    return () => clearInterval(interval);
  }, [submitted, isRoundResult]);

  const handleTimeOut = () => {
    if (submitted) return;
    setSubmitted(true);
    playWrongSound();
    onSubmit({
      answer: 'timeout',
      isCorrect: false,
      points: 0
    });
  };

  const handleSubmitWord = (e?: React.FormEvent) => {
    if (e) e.preventDefault();
    const clean = wordInput.trim().toLowerCase();
    if (!clean || submitted || isRoundResult) return;

    // Check if word is already in rally
    if (rallyWords.includes(clean)) {
      alert(`"${clean}" has already been used in this rally!`);
      return;
    }

    setSubmitted(true);
    setRallyWords((prev) => [...prev, clean]);
    playCorrectSound();

    onSubmit({
      answer: clean,
      isCorrect: true,
      points: 15
    });
  };

  return (
    <div className="flex flex-col items-center justify-between h-full max-w-2xl mx-auto space-y-6">
      {/* Tennis Court Header */}
      <div className="w-full bg-gradient-to-r from-cyan-600 to-blue-600 rounded-3xl p-6 text-white shadow-md text-center relative overflow-hidden">
        <div className="flex items-center justify-center gap-1.5 text-xs font-bold uppercase tracking-wider mb-2 text-cyan-200">
          <Activity className="w-4 h-4" />
          <span>Word Tennis • Rally Duel</span>
        </div>

        <h2 className="text-2xl md:text-3xl font-black tracking-tight my-2">
          Topic: {round.prompt.replace(/^Word Tennis topic:\s*/, '')}
        </h2>

        {/* Shot Clock */}
        <div className="mt-4 flex items-center justify-center gap-2">
          <div
            className={`px-4 py-1.5 rounded-full font-black text-sm flex items-center gap-1.5 ${
              secondsLeft <= 5
                ? 'bg-red-500 text-white animate-bounce'
                : 'bg-white/20 text-white'
            }`}
          >
            <Timer className="w-4 h-4" />
            <span>00:{secondsLeft < 10 ? `0${secondsLeft}` : secondsLeft}s</span>
          </div>
        </div>
      </div>

      {/* Input Arena */}
      <div className="w-full bg-white rounded-3xl p-6 border border-cyan-100 shadow-md space-y-4">
        <form onSubmit={handleSubmitWord} className="flex gap-2">
          <input
            type="text"
            value={wordInput}
            onChange={(e) => setWordInput(e.target.value)}
            disabled={submitted || isRoundResult}
            placeholder="Type a valid English word related to the topic..."
            className="flex-1 px-4 py-3 rounded-2xl border-2 border-gray-200 focus:outline-none focus:border-cyan-500 font-bold text-sm"
            autoFocus
          />
          <button
            type="submit"
            disabled={!wordInput.trim() || submitted || isRoundResult}
            className="px-6 py-3 rounded-2xl bg-cyan-600 hover:bg-cyan-700 text-white font-bold text-sm shadow-md transition-all disabled:opacity-40 flex items-center gap-1.5"
          >
            <span>Serve</span>
            <Send className="w-4 h-4" />
          </button>
        </form>

        <p className="text-xs text-gray-400 text-center">
          💡 Serve an English word on this topic before the shot clock runs out!
        </p>
      </div>

      {/* Rally History */}
      {rallyWords.length > 0 && (
        <div className="w-full bg-cyan-50/60 rounded-2xl p-4 border border-cyan-100">
          <p className="text-xs font-bold text-cyan-800 uppercase tracking-wider mb-2">
            Rally Shots ({rallyWords.length}):
          </p>
          <div className="flex flex-wrap gap-2">
            {rallyWords.map((w, idx) => (
              <span
                key={idx}
                className="px-3 py-1 rounded-full bg-white text-cyan-800 border border-cyan-200 text-xs font-bold shadow-xs"
              >
                🎾 {w}
              </span>
            ))}
          </div>
        </div>
      )}

      {/* Partner Status Card */}
      {partnerPlayer && (
        <div className="w-full bg-white rounded-2xl p-3 border border-gray-100 shadow-xs flex items-center justify-between text-xs">
          <span className="font-bold text-gray-800">{partnerPlayer.userName}:</span>
          <span className={partnerPlayer.roundFinished ? 'text-emerald-600 font-bold' : 'text-gray-400'}>
            {partnerPlayer.roundFinished ? '✓ Word Served!' : 'Preparing return shot...'}
          </span>
        </div>
      )}
    </div>
  );
};
